// src/components/analysis/detail/ProductAnalysisDetail.js
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Rating,
  Chip,
  Avatar
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AdjustIcon from '@mui/icons-material/Adjust';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TargetIcon from '@mui/icons-material/Adjust';
import StarIcon from '@mui/icons-material/Star';
import BuildIcon from '@mui/icons-material/Build';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

// Helper function to format keys for display
const formatEvaluationKey = (key) => {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Custom list item component for better formatting
const EnhancedListItem = ({ icon, children }) => (
  <ListItem 
    sx={{ 
      padding: 0,
      paddingY: 0.5,
      marginLeft: 0
    }}
  >
    <ListItemIcon sx={{ minWidth: 28 }}>
      {icon || <CheckCircleOutlineIcon color="primary" fontSize="small" />}
    </ListItemIcon>
    <ListItemText 
      primary={
        <Typography variant="body2">
          {children}
        </Typography>
      } 
    />
  </ListItem>
);

// Section header component
const SectionHeader = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
    <Avatar sx={{ bgcolor: 'primary.main', mr: 1.5, width: 36, height: 36 }}>
      {icon}
    </Avatar>
    <Typography variant="h6" color="primary.main" fontWeight="bold">
      {title}
    </Typography>
  </Box>
);

const ProductAnalysisDetail = ({ data }) => {
  // Access the nested objects correctly, providing fallbacks if not available
  const problem = data.problem || {};
  const solution = data.solution || {};
  const keyBenefits = data.key_benefits || {};
  const targetAudience = data.target_audience || {};
  const evaluation = data.evaluation || {};

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <SectionHeader 
              icon={<ReportProblemIcon fontSize="small" />}
              title="Problem Statement"
            />
            
            {/* Problem Section */}
            {problem.summary && (
              <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                <Typography variant="body1">
                  {problem.summary}
                </Typography>
              </Box>
            )}
            
            {/* Pain Points */}
            {problem.pain_points && problem.pain_points.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Pain Points:
                </Typography>
                <List dense disablePadding>
                  {problem.pain_points.map((point, index) => (
                    <EnhancedListItem key={index}>
                      {point}
                    </EnhancedListItem>
                  ))}
                </List>
              </Box>
            )}
            
            <SectionHeader 
              icon={<LightbulbIcon fontSize="small" />}
              title="Solution"
            />
            
            {/* Solution Summary */}
            {solution.summary && (
              <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                <Typography variant="body1">
                  {solution.summary}
                </Typography>
              </Box>
            )}
            
            {/* Target Audience */}
            {targetAudience.description && (
              <Box sx={{ mt: 4 }}>
                <SectionHeader 
                  icon={<GroupIcon fontSize="small" />}
                  title="Target Audience"
                />
                
                <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                  <Typography variant="body1">
                    {targetAudience.description}
                  </Typography>
                </Box>
                
                {targetAudience.segments && targetAudience.segments.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
                      <TargetIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Market Segments:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {targetAudience.segments.map((segment, idx) => (
                        <Chip 
                          key={idx} 
                          label={segment} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                
                {targetAudience.market_size && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingUpIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      Market Size:
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 4 }}>
                      {targetAudience.market_size}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3, height: '100%' }}>
            <SectionHeader 
              icon={<BuildIcon fontSize="small" />}
              title="Features & Technology"
            />
            
            {/* Key Features */}
            {solution.key_features && solution.key_features.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <VerifiedUserIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Key Features:
                </Typography>
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                  <List dense disablePadding>
                    {solution.key_features.map((feature, index) => (
                      <EnhancedListItem key={index}>
                        {feature}
                      </EnhancedListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            )}
            
            {/* Technology */}
            {solution.technology && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  Technology:
                </Typography>
                <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  {solution.technology}
                </Typography>
              </Box>
            )}
            
            {/* Benefits Section */}
            <Box sx={{ mt: 4 }}>
              <SectionHeader 
                icon={<StarIcon fontSize="small" />}
                title="Key Benefits & USPs"
              />
              
              {/* Benefits */}
              {keyBenefits.benefits && keyBenefits.benefits.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                    Key Benefits:
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                    <List dense disablePadding>
                      {keyBenefits.benefits.map((benefit, index) => (
                        <EnhancedListItem key={index}>
                          {benefit}
                        </EnhancedListItem>
                      ))}
                    </List>
                  </Box>
                </Box>
              )}
              
              {/* USPs */}
              {keyBenefits.unique_selling_points && keyBenefits.unique_selling_points.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocalOfferIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                    Unique Selling Points:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {keyBenefits.unique_selling_points.map((usp, index) => (
                      <Chip
                        key={index}
                        label={usp}
                        color="primary"
                        variant="outlined"
                        sx={{ justifyContent: 'flex-start', height: 'auto', padding: '8px 0', '& .MuiChip-label': { whiteSpace: 'normal' } }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              
              {/* Measurable Outcomes */}
              {keyBenefits.measurable_outcomes && keyBenefits.measurable_outcomes.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                    Measurable Outcomes:
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, border: '1px solid', borderColor: 'grey.200' }}>
                    <List dense disablePadding>
                      {keyBenefits.measurable_outcomes.map((outcome, index) => (
                        <EnhancedListItem key={index}>
                          {outcome}
                        </EnhancedListItem>
                      ))}
                    </List>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      <Divider sx={{ my: 4 }} />
      
      <Typography variant="h5" gutterBottom color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
        <StarIcon sx={{ mr: 1 }} />
        Product Evaluation
      </Typography>
      
      <Grid container spacing={3}>
        {Object.entries(evaluation)
          .filter(([key]) => key !== 'overall_product_strength')
          .map(([key, value]) => (
            <Grid item xs={12} sm={6} key={key}>
              <Card 
                variant="outlined" 
                sx={{ 
                  borderWidth: 1, 
                  borderColor: 'primary.light',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 1
                  }
                }}
              >
                <CardHeader 
                  title={formatEvaluationKey(key)} 
                  titleTypographyProps={{ fontWeight: 'bold', variant: 'subtitle1' }}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'grey.200'
                  }}
                  subheader={
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Rating 
                        value={parseFloat(value.score)} 
                        precision={0.5} 
                        readOnly 
                        emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
                      />
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          ml: 1, 
                          fontWeight: 'bold',
                          color: 'primary.main'
                        }}
                      >
                        {value.score}/5
                      </Typography>
                    </Box>
                  }
                />
                <CardContent>
                  <Typography variant="body2">
                    {value.comments}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
      </Grid>
      
      {evaluation.overall_product_strength && (
        <Box sx={{ mt: 3 }}>
          <Card 
            elevation={2}
            sx={{ 
              borderRadius: 1,
              borderLeft: 4, 
              borderColor: 'primary.main'
            }}
          >
            <CardHeader 
              title="Overall Product Assessment" 
              titleTypographyProps={{ fontWeight: 'bold', variant: 'h6', color: 'primary.main' }}
              avatar={
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <StarIcon />
                </Avatar>
              }
              subheader={
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Rating 
                    value={parseFloat(evaluation.overall_product_strength.score)} 
                    precision={0.5} 
                    readOnly 
                    sx={{ color: 'primary.main' }}
                    emptyIcon={<StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />}
                  />
                  <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold', color: 'primary.main' }}>
                    {evaluation.overall_product_strength.score}/5
                  </Typography>
                </Box>
              }
            />
            <CardContent>
              <Typography variant="body1">
                {evaluation.overall_product_strength.summary}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default ProductAnalysisDetail;